import React, { useState } from 'react'
import { useDraft } from '../useDraft'
import DraftPrompt from '../components/DraftPrompt'
import { useLanguage } from '../LanguageContext'

const VEHICLE_IDS = ['sedan', 'suv', 'van', 'maybach']

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

const EMPTY_STATE = {
  contactEmail: '',
  pickup:      '',
  dropoff:     '',
  date:        '',
  time:        '',
  passengers:  1,
  vehicle:     'sedan',
  specialReqs: '',
}

export default function CarTransfers({ onBack, member }) {
  const { t } = useLanguage()

  const [submitted,   setSubmitted]   = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const [formState, setFormState] = useState({
    ...EMPTY_STATE,
    contactEmail: member?.email || '',
  })
  const { contactEmail, pickup, dropoff, date, time, passengers, vehicle, specialReqs } = formState

  const draft = useDraft('car_transfers', formState, setFormState, EMPTY_STATE)

  // After draft restore, if contactEmail ended up empty, fall back to member email
  React.useEffect(() => {
    if (!formState.contactEmail && member?.email) {
      setFormState(prev => ({ ...prev, contactEmail: member.email }))
    }
  }, [draft.draftLoading])

  const set = (key) => (val) => {
    setFormState(prev => {
      const next = { ...prev, [key]: val }
      draft.saveProgress(next)
      return next
    })
  }

  const setInput = (key) => (e) => set(key)(e.target.value)

  const handleSubmit = (e) => { e.preventDefault(); setShowConfirm(true) }

  const handleConfirm = async () => {
    setSubmitError('')
    try {
      await draft.submitForm(formState)
      setShowConfirm(false)
      setSubmitted(true)
    } catch (_) {
      setSubmitError(t('failedSubmit'))
    }
  }

  const vehicleLabel = (id) => {
    const map = { sedan: 'vehicleSedan', suv: 'vehicleSuv', van: 'vehicleVan', maybach: 'vehicleMaybach' }
    return t(map[id] || 'vehicleSedan')
  }

  if (draft.draftLoading) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted" style={{ opacity: 0.5 }}>
          <div className="svc-submitted__icon">◌</div>
          <p className="svc-submitted__body">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (draft.draftDecision === 'pending') {
    return (
      <div className="db-page svc-page">
        <div className="svc-header">
          <button className="svc-back" onClick={onBack} aria-label={t('back')}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {t('dashboard')}
          </button>
          <div className="svc-header__title-wrap">
            <p className="svc-header__overline">{t('serviceRequest')}</p>
            <h1 className="svc-header__title">{t('serviceCarTransfers')}</h1>
          </div>
        </div>
        <DraftPrompt draft={draft.existingDraft} serviceName={t('serviceCarTransfers')} onContinue={draft.chooseContinue} onStartNew={draft.chooseNew} />
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">{t('requestReceived')}</h2>
          <p className="svc-submitted__body">{t('requestReceivedBody')}</p>
          <p className="svc-submitted__body svc-submitted__body--note">{t('carTransfersSuccessNote')}</p>
          <p className="svc-submitted__sig">{t('signatureMwuilu')}</p>
          <button className="svc-btn-ghost" onClick={onBack}>{t('returnToDashboard')}</button>
        </div>
      </div>
    )
  }

  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">{t('pleaseReview')}</p>
          <h2 className="svc-confirm__heading">{t('carTransferSingular')}</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label={t('contactEmail')}    value={contactEmail || '—'} />
            <ConfirmRow label={t('pickupLocation')}  value={pickup    || '—'} />
            <ConfirmRow label={t('destination')}     value={dropoff   || '—'} />
            <ConfirmRow label={t('date')}            value={date      || '—'} />
            <ConfirmRow label={t('pickupTime')}      value={time      || '—'} />
            <ConfirmRow label={t('passengers')}      value={`${passengers} ${passengers !== 1 ? t('passengerPlural') : t('passengerSingular')}`} />
            <ConfirmRow label={t('confirmVehicle')}  value={vehicleLabel(vehicle)} />
            {specialReqs && <ConfirmRow label={t('specialRequirements')} value={specialReqs} />}
          </div>
          {submitError && <p className="mw-error" style={{ marginBottom: 12 }}>{submitError}</p>}
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>{t('edit')}</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>{t('confirmSubmit')}</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page svc-page">
      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label={t('backToDashboard')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {t('dashboard')}
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">{t('serviceRequest')}</p>
          <h1 className="svc-header__title">{t('serviceCarTransfers')}</h1>
        </div>
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Contact email ── */}
        <div className="svc-section">
          <p className="svc-section__label">{t('contactDetails')}</p>
          <div className="svc-field">
            <label className="svc-label" htmlFor="contactEmail">{t('contactEmail')}</label>
            <input
              id="contactEmail"
              type="email"
              className="svc-input"
              value={contactEmail}
              onChange={setInput('contactEmail')}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('journey')}</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="pickup">{t('pickupLocation')}</label>
              <input id="pickup" type="text" className="svc-input" value={pickup} onChange={setInput('pickup')} placeholder={t('pickupPlaceholder')} required />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="dropoff">{t('destination')}</label>
              <input id="dropoff" type="text" className="svc-input" value={dropoff} onChange={setInput('dropoff')} placeholder={t('dropoffPlaceholder')} required />
            </div>
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('dateAndTime')}</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="transferDate">{t('date')}</label>
              <input id="transferDate" type="date" className="svc-input" value={date} onChange={setInput('date')} required />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="transferTime">{t('pickupTime')}</label>
              <input id="transferTime" type="time" className="svc-input" value={time} onChange={setInput('time')} required />
            </div>
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('passengers')}</p>
          <div className="svc-steppers">
            <div className="svc-stepper">
              <span className="svc-stepper__label">{t('passengers')}</span>
              <div className="svc-stepper__controls">
                <button type="button" className="svc-stepper__btn" onClick={() => set('passengers')(Math.max(1,  passengers - 1))} disabled={passengers <= 1} aria-label={t('decrease', { label: t('passengers') })}>−</button>
                <span className="svc-stepper__value">{passengers}</span>
                <button type="button" className="svc-stepper__btn" onClick={() => set('passengers')(Math.min(12, passengers + 1))} aria-label={t('increase', { label: t('passengers') })}>+</button>
              </div>
            </div>
          </div>
        </div>

        <div className="svc-section">
          <p className="svc-section__label">{t('vehiclePreference')}</p>
          <div className="svc-vehicle-grid">
            {VEHICLE_IDS.map(id => {
              const descKey = { sedan: 'vehicleSedanDesc', suv: 'vehicleSuvDesc', van: 'vehicleVanDesc', maybach: 'vehicleMaybachDesc' }[id]
              return (
                <button key={id} type="button" className={`svc-vehicle-card${vehicle === id ? ' svc-vehicle-card--active' : ''}`} onClick={() => set('vehicle')(id)}>
                  <span className="svc-vehicle-card__label">{vehicleLabel(id)}</span>
                  <span className="svc-vehicle-card__desc">{t(descKey)}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="transferSpecial">{t('specialRequirements')}</label>
            <textarea id="transferSpecial" className="svc-textarea" rows={3} value={specialReqs} onChange={setInput('specialReqs')} placeholder={t('transferSpecialPlaceholder')} />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">{t('reviewRequest')}</button>
        </div>

      </form>
    </div>
  )
}